export const SITE_LANGUAGE_OPTIONS = Object.freeze([
  Object.freeze({
    id: "EN",
    nativeLabel: "English",
    labelKey: "settings_language_english",
  }),
  Object.freeze({
    id: "ES",
    nativeLabel: "Español",
    labelKey: "settings_language_spanish",
  }),
]);

export const SITE_THEME_OPTIONS = Object.freeze(["light", "dark"]);

const REPORT_LANGUAGE_STORAGE_KEY = "community-report-lang";
const ACCOUNT_LANGUAGE_STORAGE_KEY = "community-accounts-lang";
const REPORT_THEME_STORAGE_KEY = "community-report-theme";
const ACCOUNT_THEME_STORAGE_KEY = "community-accounts-theme";

export function normalizeSiteLanguage(language) {
  return SITE_LANGUAGE_OPTIONS.some(({ id }) => id === language) ? language : "EN";
}

export function storedSiteLanguage(storage) {
  const stored = storage.getItem(REPORT_LANGUAGE_STORAGE_KEY)
    || storage.getItem(ACCOUNT_LANGUAGE_STORAGE_KEY);
  const normalized = normalizeSiteLanguage(stored);
  if (stored && stored !== normalized) {
    storage.setItem(REPORT_LANGUAGE_STORAGE_KEY, normalized);
  }
  return normalized;
}

export function saveSiteLanguage(storage, language) {
  const normalized = normalizeSiteLanguage(language);
  storage.setItem(REPORT_LANGUAGE_STORAGE_KEY, normalized);
  storage.setItem(ACCOUNT_LANGUAGE_STORAGE_KEY, normalized);
  return normalized;
}

export function normalizeSiteTheme(theme) {
  return SITE_THEME_OPTIONS.includes(theme) ? theme : "light";
}

export function storedSiteTheme(storage) {
  const stored = storage.getItem(REPORT_THEME_STORAGE_KEY)
    || storage.getItem(ACCOUNT_THEME_STORAGE_KEY);
  const normalized = normalizeSiteTheme(stored);
  if (stored) {
    storage.setItem(REPORT_THEME_STORAGE_KEY, normalized);
    storage.setItem(ACCOUNT_THEME_STORAGE_KEY, normalized);
  }
  return normalized;
}

export function saveSiteTheme(storage, theme) {
  const normalized = normalizeSiteTheme(theme);
  storage.setItem(REPORT_THEME_STORAGE_KEY, normalized);
  storage.setItem(ACCOUNT_THEME_STORAGE_KEY, normalized);
  return normalized;
}

function requiredElement(documentRoot, id) {
  const element = documentRoot.getElementById(id);
  if (!element) throw new Error(`Missing site settings element: ${id}`);
  return element;
}

export function initializeSiteSettingsMenu({
  documentRoot,
  storage,
  copy,
  getLanguage,
  getTheme,
  onLanguageChange,
  onThemeChange,
}) {
  const settingsButton = requiredElement(documentRoot, "settingsButton");
  const topbarMenu = requiredElement(documentRoot, "topbarMenu");
  const languageThemeMenuItem = requiredElement(documentRoot, "languageThemeMenuItem");
  const sheetBackdrop = requiredElement(documentRoot, "sheetBackdrop");
  const closeSettings = requiredElement(documentRoot, "closeSettings");
  const settingsTitle = requiredElement(documentRoot, "settingsTitle");
  const settingsLanguageLabel = requiredElement(documentRoot, "settingsLanguageLabel");
  const languageOptions = requiredElement(documentRoot, "languageOptions");
  const settingsAppearanceLabel = requiredElement(documentRoot, "settingsAppearanceLabel");
  const themeOptions = requiredElement(documentRoot, "themeOptions");

  function translated(language, key) {
    return copy(normalizeSiteLanguage(language), key);
  }

  function setMenuOpen(open, { restoreFocus = false } = {}) {
    topbarMenu.hidden = !open;
    settingsButton.setAttribute("aria-expanded", String(open));
    if (!open && restoreFocus) settingsButton.focus();
  }

  function closeSettingsSheet({ restoreFocus = true } = {}) {
    sheetBackdrop.hidden = true;
    if (restoreFocus) settingsButton.focus();
  }

  function openSettingsSheet() {
    setMenuOpen(false);
    sheetBackdrop.hidden = false;
    closeSettings.focus();
  }

  function render() {
    const language = normalizeSiteLanguage(getLanguage());
    const theme = normalizeSiteTheme(getTheme());

    documentRoot.documentElement.lang = language === "ES" ? "es" : "en";
    documentRoot.documentElement.dataset.theme = theme;
    settingsButton.setAttribute("aria-label", translated(language, "header_more"));
    languageThemeMenuItem.textContent = translated(language, "header_language_theme");
    settingsTitle.textContent = translated(language, "settings_title");
    closeSettings.setAttribute("aria-label", translated(language, "settings_close"));
    settingsLanguageLabel.textContent = translated(language, "settings_select_language");
    settingsAppearanceLabel.textContent = translated(language, "settings_appearance");

    languageOptions.innerHTML = SITE_LANGUAGE_OPTIONS
      .map(({ id, nativeLabel, labelKey }) => {
        const selected = language === id;
        return `
          <button
            class="language-option ${selected ? "selected" : ""}"
            type="button"
            data-lang="${id}"
            aria-pressed="${selected}"
          >
            <strong>${nativeLabel}<span>${translated(language, labelKey)}</span></strong>
            <span>${selected ? "+" : ""}</span>
          </button>
        `;
      })
      .join("");

    themeOptions.querySelectorAll("[data-theme]").forEach((button) => {
      const selected = button.dataset.theme === theme;
      button.classList.toggle("selected", selected);
      button.setAttribute("aria-pressed", String(selected));
      button.textContent = translated(language, `settings_theme_${button.dataset.theme}`);
    });
  }

  settingsButton.addEventListener("click", () => {
    setMenuOpen(topbarMenu.hidden);
  });

  languageThemeMenuItem.addEventListener("click", () => {
    openSettingsSheet();
  });

  closeSettings.addEventListener("click", () => {
    closeSettingsSheet();
  });

  sheetBackdrop.addEventListener("click", (event) => {
    if (event.target === sheetBackdrop) closeSettingsSheet();
  });

  documentRoot.addEventListener("click", (event) => {
    if (topbarMenu.hidden) return;
    if (event.target.closest(".topbar-menu-shell")) return;
    setMenuOpen(false);
  });

  languageOptions.addEventListener("click", (event) => {
    const target = event.target.closest("[data-lang]");
    if (!target) return;
    const language = saveSiteLanguage(storage, target.dataset.lang);
    onLanguageChange(language);
    render();
    closeSettingsSheet();
  });

  themeOptions.addEventListener("click", (event) => {
    const target = event.target.closest("[data-theme]");
    if (!target) return;
    const theme = saveSiteTheme(storage, target.dataset.theme);
    onThemeChange(theme);
    render();
  });

  documentRoot.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!sheetBackdrop.hidden) {
      closeSettingsSheet();
      return;
    }
    if (!topbarMenu.hidden) setMenuOpen(false, { restoreFocus: true });
  });

  render();

  return Object.freeze({
    closeSettingsSheet,
    openSettingsSheet,
    render,
    setMenuOpen,
  });
}
